import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import type { NlqResponse } from '@sprintpulse/shared';
import { ArrowUp, Loader2 } from 'lucide-react';
import { Chart } from 'chart.js';

const EXAMPLES = [
  'Which team had the highest velocity last sprint?',
  'Show commitment accuracy for all teams over 6 sprints',
  'Which sprints had defect escape rate above 20%?',
  'Compare carryover rate across all teams this quarter',
  'Which team has the fastest cycle time?',
];

function ResultChart({ data, type }: { data: Record<string, unknown>[]; type: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef  = useRef<Chart | null>(null);

  useEffect(() => {
    if (!canvasRef.current || !data.length) return;

    const style   = getComputedStyle(document.documentElement);
    const isDark  = document.documentElement.classList.contains('dark');
    const accent  = style.getPropertyValue('--accent').trim();
    const grid    = style.getPropertyValue('--chart-grid').trim();
    const text3   = style.getPropertyValue('--text-3').trim();
    const bgColor = isDark ? 'rgba(224,123,48,0.15)' : 'rgba(200,105,26,0.08)';
    const tooltipBg = isDark ? '#1C1C1A' : '#FFFFFF';
    const tooltipBorder = isDark ? '#2E2E2B' : '#E0DED8';
    const tooltipTitle = isDark ? '#EFEFED' : '#1A1916';
    const tooltipBody = isDark ? '#A8A69F' : '#5C5A54';

    const keys   = Object.keys(data[0]);
    const labels = data.map((r) => String(r[keys[0]] ?? ''));
    const values = data.map((r) => Number(r[keys[1]] ?? 0));

    if (chartRef.current) chartRef.current.destroy();

    const isLine = type === 'line';

    chartRef.current = new Chart(canvasRef.current, {
      type: isLine ? 'line' : 'bar',
      data: {
        labels,
        datasets: [{
          data: values,
          backgroundColor: isLine ? bgColor : accent,
          borderColor: accent,
          borderWidth: isLine ? 1.5 : 0,
          borderRadius: isLine ? 0 : 3,
          pointRadius: isLine ? 3 : 0,
          pointBackgroundColor: accent,
          fill: isLine,
          tension: 0.3,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 250 },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: tooltipBg,
            borderColor: tooltipBorder,
            borderWidth: 1,
            titleColor: tooltipTitle,
            bodyColor: tooltipBody,
            padding: 10,
            cornerRadius: 6,
          },
        },
        scales: {
          x: {
            border: { display: false },
            grid: { display: false },
            ticks: { color: text3, font: { size: 11, family: '"SF Mono", monospace' }, maxRotation: 30 },
          },
          y: {
            border: { display: false },
            grid: { color: grid, lineWidth: 1 },
            ticks: { color: text3, font: { size: 11, family: '"SF Mono", monospace' }, maxTicksLimit: 5 },
          },
        },
      },
    });

    return () => { chartRef.current?.destroy(); };
  }, [data, type]);

  return (
    <div style={{ position: 'relative', height: '200px', width: '100%' }}>
      <canvas ref={canvasRef} />
    </div>
  );
}

export function NlqPage(): JSX.Element {
  const [input, setInput]     = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState<NlqResponse | null>(null);
  const [error, setError]     = useState<string | null>(null);
  const [history, setHistory] = useState<{ query: string; answer: string }[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = async (q = input): Promise<void> => {
    if (!q.trim() || loading) return;
    setInput(q);
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.nlq({ query: q });
      setResult(res);
      setHistory((prev) => [{ query: q, answer: res.answer }, ...prev.slice(0, 4)]);
    } catch {
      setError('Request failed — check that the backend is running and Bedrock is configured.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--text-1)' }}>
          Ask anything
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-3)' }}>
          Natural language queries over your sprint data
        </p>
      </div>

      {/* Input */}
      <div
        className="flex items-center gap-3 p-3 rounded-lg border mb-4"
        style={{ background: 'var(--bg-panel)', borderColor: 'var(--border)' }}
      >
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder="Which team improved the most last sprint?"
          className="flex-1 bg-transparent text-sm outline-none placeholder:opacity-40"
          style={{ color: 'var(--text-1)' }}
        />
        <button
          onClick={() => submit()}
          disabled={loading || !input.trim()}
          className="shrink-0 w-8 h-8 rounded flex items-center justify-center transition-opacity disabled:opacity-30"
          style={{ background: 'var(--accent)', color: '#fff' }}
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowUp size={14} />}
        </button>
      </div>

      {/* Example chips */}
      <div className="flex flex-wrap gap-2 mb-8">
        {EXAMPLES.map((q) => (
          <button
            key={q}
            onClick={() => submit(q)}
            className="text-xs px-3 py-1.5 rounded-full border transition-colors hover:border-current"
            style={{
              borderColor: 'var(--border)',
              color: 'var(--text-2)',
            }}
          >
            {q}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div
          className="rounded-lg p-4 mb-6 text-sm"
          style={{ background: 'var(--red-dim)', color: 'var(--red)' }}
        >
          {error}
        </div>
      )}

      {/* Result */}
      {result && (
        <div
          className="rounded-lg border p-6 mb-6"
          style={{ background: 'var(--bg-panel)', borderColor: 'var(--border)' }}
        >
          <p className="text-xs mb-3 font-mono" style={{ color: 'var(--text-3)' }}>
            "{result.query}"
          </p>
          <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--text-1)' }}>
            {result.answer}
          </p>

          {result.data && result.data.length > 0 && result.chartSuggestion !== 'table' && (
            <div className="mb-5">
              <ResultChart data={result.data as Record<string, unknown>[]} type={result.chartSuggestion ?? 'bar'} />
            </div>
          )}

          {result.data && result.data.length > 0 && (
            <details>
              <summary
                className="text-xs cursor-pointer select-none"
                style={{ color: 'var(--text-3)' }}
              >
                Raw data · {result.data.length} rows
              </summary>
              <div className="mt-3 overflow-x-auto rounded border" style={{ borderColor: 'var(--border)' }}>
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ background: 'var(--bg-subtle)' }}>
                      {Object.keys(result.data[0]).map((k) => (
                        <th
                          key={k}
                          className="text-left px-3 py-2 font-medium font-mono border-b"
                          style={{ color: 'var(--text-2)', borderColor: 'var(--border)' }}
                        >
                          {k}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.data.slice(0, 20).map((row, i) => (
                      <tr
                        key={i}
                        style={{
                          background: i % 2 === 0 ? 'var(--bg-panel)' : 'var(--bg-subtle)',
                        }}
                      >
                        {Object.values(row).map((v, j) => (
                          <td
                            key={j}
                            className="px-3 py-2 font-mono border-b"
                            style={{ color: 'var(--text-2)', borderColor: 'var(--border)' }}
                          >
                            {String(v ?? '—')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--text-3)' }}>
            Recent
          </p>
          <div className="space-y-2">
            {history.map((h, i) => (
              <button
                key={i}
                onClick={() => submit(h.query)}
                className="w-full text-left p-3 rounded-lg border transition-colors hover:border-current"
                style={{
                  background: 'var(--bg-panel)',
                  borderColor: 'var(--border)',
                }}
              >
                <p className="text-xs font-medium font-mono" style={{ color: 'var(--accent)' }}>
                  {h.query}
                </p>
                <p
                  className="text-xs mt-1 line-clamp-2"
                  style={{ color: 'var(--text-3)' }}
                >
                  {h.answer}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}